<template>
    <DataTable
        v-model="selectedRows"
        :header="$t('invoices')"
        :fields="fields"
        :items="parentComponent.items"
        :loading="parentComponent.loading"
        :error="parentComponent.error"
        :total-rows="parentComponent.requestData.max"
        sort-by="number"
        sort-direction="DESC"
        no-sort-reset
        selectable
        selectable-id-key="id"
        :busy-items="busyItems"
        :foot-clone="!!parentComponent.items.length"
        no-footer-sorting
    >
        <template #cell(items)="data">
            {{ formatShops(data.item.items) }}
        </template>
        <template #cell(number)="data">
            <router-link :to="{ name: 'SettlementsInvoicesDetails', params: { id: data.item.id, parentUrl: $route.fullPath } }">
                {{ data.item.number }}
            </router-link>
        </template>

        <template #cell(createdAt)="data">
            {{ $format.date(data.item.createdAt, 'dd-MM-yyyy') }}
        </template>

        <template #cell(dueDate)="data">
            {{ $format.date(data.item.dueDate, 'dd-MM-yyyy') }}
        </template>
        <template #cell(settlementNetAmount)="data">
            {{ $format.currency(data.value) }}
        </template>

        <template #cell(netAmount)="data">
            {{ $format.currency(data.value) }}
        </template>

        <template #cell(grossAmount)="data">
            {{ $format.currency(data.value) }}
        </template>

        <template #cell(status)="data">
            <BadgeFinanceDocumentStatus :status="new String(data.value)" />
        </template>

        <template #foot()>
            <div></div>
        </template>
        <template #foot(settlementNetAmount)>
            {{ $format.currency(sumBy(parentComponent.items, 'settlementNetAmount')) }}
        </template>
        <template #foot(netAmount)>
            {{ $format.currency(sumBy(parentComponent.items, 'netAmount')) }}
        </template>
        <template #foot(grossAmount)>
            {{ $format.currency(sumBy(parentComponent.items, 'grossAmount')) }}
        </template>

        <template #afterTable>
            <div class="d-flex align-items-center gap-2 mt-3">
                <Multiselect
                    v-model="tableAction"
                    :options="tableActionOptions"
                    select-label=""
                    selected-label=""
                    deselect-label=""
                    class="width-250 d-inline-block"
                />
                <ButtonIcon :disabled="!tableAction || !tableAction.value || !parentComponent.items.length || parentComponent.loading || !selectedRows.length" :loading="requestInvoicesSettle.getAnyLoading() || requestInvoicesAbandon.getAnyLoading()" hide-label-while-loading @click="initTableAction()">
                    {{ $t('save') }}
                </ButtonIcon>
            </div>
        </template>
    </DataTable>
</template>



<script>
    import sumBy from 'lodash/sumBy';
    import DataTable from '@/components/common/DataTable.vue';
    import BadgeFinanceDocumentStatus from '@/components/common/BadgeFinanceDocumentStatus.vue';
    import Multiselect from '@/components/common/Multiselect.vue';
    import without from 'lodash/without';

    export default {
        name: 'Results',
        components: {
            DataTable,
            BadgeFinanceDocumentStatus,
            Multiselect,
        },
        inject: {
            parentComponent: {
                default: null,
            },
        },
        data() {
            return {
                requestInvoicesSettle: this.$req('advertiserSettlements/InvoicesSettle'),
                requestInvoicesAbandon: this.$req('advertiserSettlements/InvoicesAbandon'),
                busyItems: [],
                selectedRows: [],
                tableAction: null,
                tableActionOptions: [
                    { value: null, text: this.$t('select') },
                    { value: 'settle', text: this.$t('settleInvoice') },
                    { value: 'abandon', text: this.$t('abandonInvoice') },
                ],
            };
        },
        computed: {
            fields() {
                return [
                    {
                        key: 'number',
                        label: this.$t('document'),
                        sortable: true,
                    },
                    {
                        key: 'advertiser.company',
                        label: this.$t('advertiser'),
                        sortable: true,
                    },
                    {
                        key: 'items',
                        label: this.$t('campaign'),
                        sortable: true,
                    },
                    {
                        key: 'createdAt',
                        label: this.$t('issueDate'),
                        sortable: true,
                        tdClass: 'text-right text-nowrap',
                        thClass: 'text-right text-nowrap',
                    },
                    {
                        key: 'dueDate',
                        label: this.$t('dueDate'),
                        sortable: true,
                        tdClass: 'text-right text-nowrap',
                        thClass: 'text-right text-nowrap',
                    },
                    {
                        key: 'settlementNetAmount',
                        label: this.$t('netSettlementAmount'),
                        sortable: true,
                        tdClass: 'text-right text-nowrap',
                        thClass: 'text-right text-nowrap',
                    },
                    {
                        key: 'netAmount',
                        label: this.$t('netAmount'),
                        sortable: true,
                        tdClass: 'text-right text-nowrap',
                        thClass: 'text-right text-nowrap',
                    },
                    {
                        key: 'grossAmount',
                        label: this.$t('grossAmount'),
                        sortable: true,
                        tdClass: 'text-right text-nowrap',
                        thClass: 'text-right text-nowrap',
                    },
                    {
                        key: 'status',
                        label: this.$t('status'),
                        sortable: true,
                    },
                ];
            },
        },
        methods: {
            sumBy,
            formatShops(shops) {
                if (!shops) return '';

                return shops.map(item => item.campaign?.name).join(', ');
            },
            initTableAction() {
                if (!this.selectedRows.length) return;

                this.busyItems = [...this.selectedRows];

                if (this.tableAction.value === 'settle') {
                    this.selectedRows.forEach(this.invoicesSettle);
                } else if (this.tableAction.value === 'abandon') {
                    this.selectedRows.forEach(this.invoicesAbandon);
                } else {
                    this.busyItems = [];
                }
            },
            invoicesSettle(id) {
                return this.requestInvoicesSettle
                    .id(id)
                    .put({ urlParams: { id } })
                    .then(data => this.updateItem(id, data))
                    .then(() => (this.selectedRows = without(this.selectedRows, id)))
                    .catch(error => this.$toastr.error(error.message, this.$t('errorOccurred'), { id }))
                    .finally(() => (this.busyItems = without(this.busyItems, id)));
            },
            invoicesAbandon(id) {
                return this.requestInvoicesAbandon
                    .id(id)
                    .put({ urlParams: { id } })
                    .then(data => this.updateItem(id, data))
                    .then(() => (this.selectedRows = without(this.selectedRows, id)))
                    .catch(error => this.$toastr.error(error.message, this.$t('errorOccurred'), { id }))
                    .finally(() => (this.busyItems = without(this.busyItems, id)));
            },
            updateItem(id, data) {
                const items = this.parentComponent.request.getData()?.items || [];
                const index = items.findIndex(item => item.id === id);
                if (index !== -1) {
                    this.$set(items, index, data);
                }
                return Promise.resolve();
            },
        },
    };
</script>
